import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, absUrl } from "@/lib/api";
import { ShieldCheck, MapPin, Package, Clock, Building2, Sparkles, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useCurrency } from "@/contexts/CurrencyContext";

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [activeImg, setActiveImg] = useState("");
  const [rfqOpen, setRfqOpen] = useState(false);
  const { formatRange } = useCurrency();

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setP(data);
      setActiveImg(data.image_url || (data.gallery && data.gallery[0]) || "");
    }).catch(() => toast.error("Product not found"));
  }, [id]);

  // Inject JSON-LD Product schema for Google rich results
  useEffect(() => {
    if (!p) return;
    const supplier = p.supplier || {};
    const gallery = (p.gallery && p.gallery.length > 0)
      ? p.gallery
      : (p.image_url ? [p.image_url] : []);
    const images = gallery
      .map((g) => (g && (g.startsWith("http") ? g : absUrl(g))))
      .filter(Boolean);

    const offers = {};
    if (p.price_min || p.price_max) {
      offers["@type"] = (p.price_min && p.price_max && p.price_min !== p.price_max) ? "AggregateOffer" : "Offer";
      offers.priceCurrency = p.currency || "USD";
      if (offers["@type"] === "AggregateOffer") {
        offers.lowPrice = p.price_min;
        offers.highPrice = p.price_max;
      } else {
        offers.price = p.price_min || p.price_max;
      }
      offers.availability = "https://schema.org/InStock";
      if (supplier.name || supplier.company) {
        offers.seller = { "@type": "Organization", name: supplier.name || supplier.company };
      }
    }

    const schema = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: p.title,
      description: p.description || "",
      ...(images.length ? { image: images } : {}),
      ...(p.tags && p.tags.length ? { keywords: p.tags.join(", ") } : {}),
      ...((supplier.name || supplier.company) ? {
        brand: { "@type": "Brand", name: supplier.name || supplier.company },
      } : {}),
      ...(Object.keys(offers).length ? { offers } : {}),
      url: typeof window !== "undefined" ? window.location.href : undefined,
    };

    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-testid", "product-jsonld");
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);

    // Also set the document title for SEO
    const prevTitle = document.title;
    document.title = `${p.title} — ${supplier.name || supplier.company || "Accommodation Suppliers"}`;

    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
      document.title = prevTitle;
    };
  }, [p]);

  if (!p) return <div className="max-w-7xl mx-auto px-4 py-16 text-[#7D8C85]">Loading…</div>;
  const gallery = (p.gallery && p.gallery.length > 0) ? p.gallery : (p.image_url ? [p.image_url] : []);
  const supplier = p.supplier || {};
  const specs = p.specs || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="product-page">
      {/* breadcrumb */}
      <div className="flex items-center text-sm text-[#7D8C85] mb-6 flex-wrap">
        <Link to="/categories" className="hover:text-[#264839]">Categories</Link>
        {supplier.id && (
          <>
            <ChevronRight className="w-3 h-3 mx-2" />
            <Link to={`/listing/${p.listing?.slug || p.listing_id}`} className="hover:text-[#264839]">{supplier.name || supplier.company || "Supplier"}</Link>
          </>
        )}
        <ChevronRight className="w-3 h-3 mx-2" />
        <span className="text-[#11211A] truncate">{p.title}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        {/* Gallery */}
        <div className="lg:col-span-3">
          <div className="aspect-[4/3] bg-[#F0EFEA] overflow-hidden" data-testid="product-hero">
            {activeImg
              ? <img src={absUrl(activeImg)} alt={p.title} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-[#C4CFC9]">No image</div>}
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {gallery.map((g) => (
                <button
                  key={g}
                  onClick={() => setActiveImg(g)}
                  className={`aspect-[4/3] bg-[#F0EFEA] overflow-hidden border ${activeImg === g ? "border-[#264839]" : "border-transparent"} hover:border-[#264839]`}
                  data-testid="product-gallery-thumb"
                >
                  <img src={absUrl(g)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="lg:col-span-2">
          <div className="tracking-overline mb-2">{supplier.name || supplier.company || "Supplier"}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#11211A] tracking-tight mb-4" data-testid="product-title">{p.title}</h1>

          {(p.price_min || p.price_max) && (
            <div className="text-2xl font-serif text-[#11211A] mb-6" data-testid="product-price">
              {formatRange
                ? formatRange(p.price_min, p.price_max, p.currency || "USD")
                : `${p.price_min ? `$${p.price_min}` : ""}${p.price_max ? ` – $${p.price_max}` : ""} ${p.currency || "USD"}`}
            </div>
          )}

          <p className="text-[#4A5550] leading-relaxed mb-6">{p.description}</p>

          <div className="grid grid-cols-2 gap-3 text-sm mb-6">
            {p.moq && <div><div className="text-[#7D8C85] text-xs">MOQ</div><div className="text-[#11211A]">{p.moq}</div></div>}
            {p.lead_time && <div><div className="text-[#7D8C85] text-xs">Lead time</div><div className="text-[#11211A]">{p.lead_time}</div></div>}
          </div>

          {Object.keys(specs).length > 0 && (
            <div className="border-t border-[#E8ECE9] pt-6 mb-6" data-testid="product-specs">
              <div className="tracking-overline mb-3">Specifications</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(specs).map(([k, v]) => (
                    <tr key={k} className="border-b border-[#F0EFEA] last:border-0">
                      <td className="text-[#7D8C85] py-2 pr-4 align-top w-1/3">{k}</td>
                      <td className="text-[#11211A] py-2">{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {p.tags && p.tags.length > 0 && (
            <div className="mb-6">
              <div className="tracking-overline mb-2">Tags</div>
              <div className="flex flex-wrap gap-2">
                {p.tags.map((t) => <span key={t} className="text-xs bg-[#F0EFEA] px-2 py-1 text-[#4A5550]">{t}</span>)}
              </div>
            </div>
          )}

          <button onClick={() => setRfqOpen(true)} className="btn-primary w-full justify-center" data-testid="product-request-info">
            <Sparkles className="w-4 h-4" /> Request Quote for this product
          </button>

          {/* Supplier mini-card */}
          <div className="mt-8 border-t border-[#E8ECE9] pt-6">
            <div className="tracking-overline mb-3">Sold by</div>
            <Link to={`/listing/${p.listing?.slug || p.listing_id}`} className="flex items-start gap-3 group" data-testid="product-supplier-link">
              <div className="w-12 h-12 bg-[#F0EFEA] flex items-center justify-center border border-[#E8ECE9] shrink-0">
                {supplier.logo_url
                  ? <img src={absUrl(supplier.logo_url)} alt="" className="w-full h-full object-contain" />
                  : <Building2 className="w-5 h-5 text-[#7D8C85]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-[#11211A] group-hover:text-[#264839]">{supplier.name || supplier.company}</div>
                  {supplier.is_verified && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#264839] bg-[#E8ECE9] px-2 py-0.5">
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                  {p.brand_profile && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#B35C4A] bg-[#F0EFEA] px-2 py-0.5" data-testid="product-brand-label">
                      Brand · {p.brand_profile.name}
                    </span>
                  )}
                </div>
                {(supplier.state_codes?.length || supplier.country_code) && (
                  <div className="flex items-center gap-1 text-xs text-[#7D8C85] mt-1">
                    <MapPin className="w-3 h-3" />
                    {supplier.state_codes?.length ? supplier.state_codes.join(" / ") : "Nationwide"}
                    {supplier.country_code ? ` · ${supplier.country_code}` : ""}
                  </div>
                )}
                {supplier.supplier_type && <div className="text-xs text-[#7D8C85] mt-0.5">{supplier.supplier_type}</div>}
              </div>
            </Link>
          </div>
        </div>
      </div>

      <ProductRequestInfoModal open={rfqOpen} onClose={() => setRfqOpen(false)} product={p} />
    </div>
  );
}


function ProductRequestInfoModal({ open, onClose, product }) {
  const [form, setForm] = useState({ name: "", email: "", website: "", phone: "", company: "", hotel: "", message: "", quantity: "" });
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/request-info", {
        listing_id: product.listing_id,
        customer_name: form.name,
        customer_email: form.email,
        customer_website: form.website,
        customer_phone: form.phone,
        hotel: form.hotel,
        message: form.message && form.message.trim().length > 0
          ? form.message
          : `Please share more information about ${product.title}${form.quantity ? ` (quantity of interest: ${form.quantity})` : ""}.`,
      });
      toast.success("Request sent — the supplier will respond directly.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSaving(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="product-request-modal">
      <div className="bg-white max-w-lg w-full">
        <div className="flex justify-between p-6 border-b border-[#E8ECE9]">
          <div>
            <div className="tracking-overline">Request info</div>
            <div className="font-serif text-xl text-[#11211A]">{product.title}</div>
          </div>
          <button onClick={onClose} className="text-[#7D8C85] text-2xl leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-3">
          <input required placeholder="Your name" value={form.name} onChange={set("name")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="info-name" />
          <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="info-email" />
          <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} className="px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="info-phone" />
          <input placeholder="Web address (optional)" value={form.website} onChange={set("website")} className="px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="info-website" />
          <input placeholder="Hotel / property" value={form.hotel} onChange={set("hotel")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="info-hotel" />
          <input placeholder="Quantity / MOQ interest" value={form.quantity} onChange={set("quantity")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="info-quantity" />
          <textarea rows={4} placeholder="Notes / questions" value={form.message} onChange={set("message")} className="col-span-2 px-3 py-2 border border-[#C4CFC9] rounded-sm" data-testid="info-message" />
          <button disabled={saving} className="col-span-2 btn-primary justify-center" data-testid="info-submit">{saving ? "Sending…" : "Send request"}</button>
        </form>
      </div>
    </div>
  );
}
